import { Inject, Injectable } from "@angular/core";

import { SessionManagerService } from "./session-manager.service";
import { SessionManagerServiceConfig } from "../model/session-manager.service.config.model";
import { selectSessionState, SessionState } from "../model/session.state.model";
import { SessionActionFactory, SessionActionFactoryToken } from "../action/session.action.factory";
import { Store } from "@ngrx/store";
import { LocalStoreService } from "../../../../core/utils/local-store.service";


@Injectable()
export class SessionManagerServiceImpl implements SessionManagerService {
    protected _config: SessionManagerServiceConfig = {
        reviveDelayInSec: 3
    };

    protected _sessionState: SessionState;

    protected _sessionActionFactory: SessionActionFactory;

    protected _timeoutId: any;

    constructor(
        ngrxStore: Store<any>,
        @Inject(SessionActionFactoryToken)
        sessionActionFactory: SessionActionFactory
    ) {
        this._sessionActionFactory = sessionActionFactory;

        ngrxStore.select(selectSessionState).subscribe(this.manageSession.bind(this));
    }

    /**
     * This method initialize the session manager service and resets the session time counter to configured session
     * timeout value.
     */
    public init(): void {
        this.resetSession();
    }

    /**
     * This method resets the session time counter to configured session timeout value.
     */
    public resetSession(sessionTime?: number): void {
        this._sessionActionFactory.sessionReset(sessionTime ? sessionTime : null);
    }

    /**
     * This method resets the revive timeout counter. It should be used on every user activity. If the revive timeout
     * counter counts down to 0 then the session revive endpoint should be called on the backend. But it is not called
     * by this service.
     */
    public resetSessionRevive(): void {
        this._sessionActionFactory.reviveReset(this._config.reviveDelayInSec);
    }

    /**
     * This method collects all the tasks that should be performed on every user activity. Mostly it resets the session
     * revive, but there could be other tasks as well.
     */
    public onUserEvent(): void {
        if (this._sessionState && this._sessionState.remainingTime > 0) {
            this.resetSessionRevive();
        }
    }

    /**
     * This method will initiate all the tasks that should be performed when a fatal error occurs in the application.
     */
    public onFatalError(): void {
        this._sessionActionFactory.fatalError();
    }

    protected manageSession(sessionState: SessionState): void {
        this._sessionState = sessionState;

        if (this._sessionState  && this._sessionState.remainingTime > -1) {
            this.clearTickTimeout();
            this._timeoutId = setTimeout(this._sessionActionFactory.sessionTick.bind(this._sessionActionFactory), 1000);
        }
    }

    protected clearTickTimeout(): void {
        if (this._timeoutId) {
            clearTimeout(this._timeoutId);
        }
    }

    public isSessionActive(): boolean {
        return this._sessionState ? this._sessionState.remainingTime > 0 : false;
    }

    /**
     * This method set, session timeout warning time.
     */
    public setTimeoutWarningTime(sessionTimeOut: number): void {
        this._sessionActionFactory.setTimeoutWarningTime(sessionTimeOut);
    }

    restoreTimeOuts(): void {
      const sto = LocalStoreService.getSessionTimeOut();
      if (sto > 0) {
        this.resetSession(sto);
      }
      const swto = LocalStoreService.getSessionWarningTime();
      if (swto > 0) {
        this.setTimeoutWarningTime(swto);
      }
    }
}
